<script>
import api from '@/helpers/api'
import moment from 'moment'
import { authComputed } from '@/state/helpers'
import _sortBy from 'lodash/sortBy'
import { ordersByType } from './dev-data'

export default {
  name: 'quick-add',
  props: {
    buttonLabel: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: 'button',
      validator: value => ['button', 'card'].includes(value)
    }
  },
  data () {
    return {
      formsData: [],
      isLoading: false,
      selectedForm: {},
      showForm: false,
      mostUsedType: null
    }
  },
  mounted () {
    this.onRefresh()
  },
  computed: {
    favoriteForms () {
      return this.formsData.filter(a => a.is_favorite)
    },
    restForms () {
      return this.formsData.filter(a => !a.is_favorite)
    },
    filters () {
      return [
        { is_webshop_item: { _eq: true } },
        { is_discontinued: { _eq: false } },
        { webshop_custom_form: { _is_null: false } }
      ]
    },
    label () {
      return this.buttonLabel || this.$t('dashboard.new_order')
    },
    mostUsedForm () {
      if (!this.formsData.length) return null
      if (this.mostUsedType) {
        const match = this.formsData.find(
          form => form.webshop_custom_form === this.mostUsedType
        )
        if (match) return match
      }
      if (this.favoriteForms.length) return this.favoriteForms[0]
      return this.formsData[0]
    },
    mostUsedFormLabel () {
      if (this.mostUsedForm && this.mostUsedForm.short_description) {
        return this.mostUsedForm.short_description
      }
      if (this.mostUsedType) {
        return this.typeLabel(this.mostUsedType)
      }
      return this.$t('dashboard.new_order_card.loading')
    },
    ...authComputed
  },
  methods: {
    setSelectedForm (form) {
      if (!form || !form.id) return
      this.selectedForm = form
      this.showForm = true
    },
    openMostUsedForm () {
      this.setSelectedForm(this.mostUsedForm)
    },
    typeLabel (type) {
      const key = `forms.${type}`
      const translated = this.$t(key)
      if (translated !== key) return translated
      return type
    },
    async resolveMostUsedType () {
      try {
        const year = moment().year()
        const stats = await api.webForms.fetchModelTypeStats({ year })
        const byType = {}

        ;(stats || []).forEach(row => {
          if (!row.type) return
          byType[row.type] = (byType[row.type] || 0) + (row.quantity || 0)
        })

        const top = Object.keys(byType).sort(
          (a, b) => byType[b] - byType[a]
        )[0]

        this.mostUsedType =
          top ||
          (process.env.NODE_ENV !== 'production'
            ? (ordersByType[0] && ordersByType[0].type) || 'custom_seat'
            : null)
      } catch (e) {
        this.mostUsedType =
          process.env.NODE_ENV !== 'production'
            ? (ordersByType[0] && ordersByType[0].type) || 'custom_seat'
            : null
      }
    },
    async onRefresh () {
      this.isLoading = true

      const [{ items }] = await Promise.all([
        api.items.fetchPage({
          page: 0,
          pageSize: -1,
          filters: [...this.filters]
        }),
        this.resolveMostUsedType()
      ])

      const favorite_products =
        (this.userProfile.preferences &&
          this.userProfile.preferences.favorite_products) ||
        []

      var sortedItems = _sortBy(
        [
          ...items.map(p => ({
            ...p,
            is_favorite: favorite_products.includes(p.id)
          }))
        ],
        p => !p.is_favorite
      )

      this.formsData = sortedItems.filter(
        item => item.webshop_custom_form !== null
      )
      this.isLoading = false
    }
  }
}
</script>
<template>
  <div :class="{ 'new-order-card-wrap': variant === 'card' }">
    <button
      v-if="variant === 'card'"
      type="button"
      class="new-order-card"
      :disabled="isLoading || !mostUsedForm"
      @click="openMostUsedForm"
    >
      <div class="new-order-card__body">
        <div class="new-order-card__copy">
          <div class="new-order-card__eyebrow">
            {{ $t('dashboard.new_order') }}
          </div>
          <div class="new-order-card__value">{{ mostUsedFormLabel }}</div>
          <div class="new-order-card__hint">
            {{ $t('dashboard.new_order_card.hint') }}
          </div>
        </div>
        <span class="new-order-card__icon">
          <i class="mdi mdi-plus"></i>
        </span>
      </div>
    </button>

    <b-dropdown
      v-else
      toggle-class="btn portal-cta-btn"
      variant="primary"
      class="portal-cta"
    >
      <template #button-content>
        <i class="fab fa-wpforms label-icon"></i>{{ label }}
      </template>
      <b-dropdown-group id="dropdown-group-1" :header="$t('Favorites')">
        <template v-for="form in favoriteForms">
          <b-dropdown-item :key="form.id" @click="setSelectedForm(form)">
            {{ form.short_description }}
          </b-dropdown-item>
        </template>
      </b-dropdown-group>
      <b-dropdown-divider></b-dropdown-divider>
      <template v-for="form in restForms">
        <b-dropdown-item :key="form.id" @click="setSelectedForm(form)">
          {{ form.short_description }}
        </b-dropdown-item>
      </template>
    </b-dropdown>

    <app-add-to-cart-form-modal
      :product-id="selectedForm.id"
      :product="selectedForm"
      :visible.sync="showForm"
    />
  </div>
</template>

<style lang="scss" scoped>
.portal-cta {
  ::v-deep .portal-cta-btn {
    border-radius: 0.75rem !important;
    padding: 0.55rem 1rem !important;
    font-weight: 600;
    box-shadow: 0 8px 18px rgba(85, 110, 230, 0.25);
  }
}

.new-order-card-wrap {
  height: 100%;
}

.new-order-card {
  appearance: none;
  border: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
}

.new-order-card__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  height: 100%;
  min-height: 6.5rem;
  text-align: left;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
  padding: 1.15rem 1.25rem;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  .new-order-card:hover:not(:disabled) & {
    transform: translateY(-1px);
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
  }
}

.new-order-card__eyebrow {
  color: #94a3b8;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 0.35rem;
}

.new-order-card__value {
  color: #0f172a;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.new-order-card__hint {
  margin-top: 0.45rem;
  font-size: 0.78rem;
  color: #64748b;
}

.new-order-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 999px;
  background: rgba(85, 110, 230, 0.12);
  color: #556ee6;
  font-size: 1.4rem;
  flex: 0 0 auto;
}
</style>
